import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { formatUsdFull, formatWibLong } from "@po/shared";
import type { approvalLimitDetail } from "../../../api/src/approval-limits";
import { DataTable } from "../components/DataTable";
import { api } from "../lib/api";
import { useDashboard } from "../pages/Dashboard";

type Detail = NonNullable<Awaited<ReturnType<typeof approvalLimitDetail>>>;
type Status = "match" | "different" | "missing" | "extra" | null;

/** Sertica's "no limit": every limit from it up counts as unlimited. */
const UNLIMITED = 999_999_999;
const same = (a: number, b: number) => Math.abs(Math.min(a, UNLIMITED) - Math.min(b, UNLIMITED)) < 0.005;
const idrText = (n: number) => `Rp${Math.round(n).toLocaleString("id-ID")}`;
const usdText = (n: number) => (n >= UNLIMITED ? "Unlimited" : formatUsdFull(n));

const STATUS: Record<Exclude<Status, null>, { label: string; cls: string }> = {
  match: { label: "Matches", cls: "text-[var(--stage-received)]" },
  different: { label: "Different in Sertica", cls: "text-[var(--budget-warn)] font-semibold" },
  missing: { label: "Not in Sertica", cls: "text-[var(--budget-warn)] font-semibold" },
  extra: { label: "In Sertica, not assigned", cls: "text-[var(--budget-warn)] font-semibold" },
};

/**
 * Approval limits (SPEC §8.9): one standard limit per approver role, the same for every company it
 * is assigned to, set here by admins. Sertica is not written: each role lists where Sertica's
 * company approvers differ, for fixing there.
 */
export function ApprovalLimitsTab() {
  const [role, setRole] = useState<string | null>(null);
  return (
    <div className="space-y-4">
      <p className="text-[11.5px] text-muted">
        Each approver role&apos;s standard approval limit, the same for every company the role is assigned
        to, in USD and in IDR at Sertica&apos;s current rate. Admins set the limit and the companies; the
        dashboard does not change Sertica, it lists the companies where Sertica&apos;s approver limit
        differs, so they can be fixed there. Click a role to see or set it. The search matches the role.
      </p>
      {role && <RoleLimit key={role} roleNo={role} onClose={() => setRole(null)} />}
      <DataTable
        table="approval-limits"
        title="Approval limits"
        lens="plain"
        onRow={(r) => setRole(String(r.no))}
        highlight={(r) => (Number(r.mismatchCount) > 0 ? ["mismatchCount", "mismatches"] : [])}
      />
    </div>
  );
}

function RoleLimit({ roleNo, onClose }: { roleNo: string; onClose: () => void }) {
  const { me } = useDashboard();
  const qc = useQueryClient();
  const ref = useRef<HTMLElement>(null);
  const q = useQuery({
    queryKey: ["approval-limit", roleNo],
    queryFn: () => api<Detail>(`/approval-limits/${encodeURIComponent(roleNo)}`),
  });
  const d = q.data;
  const [usd, setUsd] = useState("");
  const [unlimited, setUnlimited] = useState(false);
  const [assigned, setAssigned] = useState<Set<string>>(new Set());
  const [onlyDiff, setOnlyDiff] = useState(false);

  // The saved standard; before one is set, Sertica's most common limit and the companies with the role.
  const reset = (x: Detail) => {
    const limit = x.limitUsd ?? x.suggestedUsd;
    setUnlimited(limit != null && limit >= UNLIMITED);
    setUsd(limit != null && limit < UNLIMITED ? String(limit) : "");
    setAssigned(new Set(x.companies.filter((c) => (x.limitUsd == null ? c.serticaUsd != null : c.assigned)).map((c) => c.no)));
  };
  useEffect(() => {
    if (d) reset(d);
  }, [d]);
  // Braced: newer browsers' scrollIntoView returns a promise, which React would take for a cleanup.
  useEffect(() => {
    ref.current?.scrollIntoView({ block: "nearest" });
  }, []);

  const save = useMutation({
    mutationFn: (body: { limitUsd: number | null; companies: string[] }) =>
      api<Detail>(`/admin/approval-limits/${encodeURIComponent(roleNo)}`, { method: "PUT", body: JSON.stringify(body) }),
    onSuccess: (x) => {
      qc.setQueryData(["approval-limit", roleNo], x);
      void qc.invalidateQueries({ queryKey: ["table", "approval-limits"] });
    },
  });

  const rate = d?.idrPerUsd ?? null;
  const limit = unlimited ? UNLIMITED : usd.trim() === "" ? null : Number(usd);
  const valid = limit != null && Number.isFinite(limit) && limit >= 0;
  const statusOf = (c: Detail["companies"][number]): Status =>
    !valid
      ? null
      : assigned.has(c.no)
        ? c.serticaUsd == null
          ? "missing"
          : same(c.serticaUsd, limit)
            ? "match"
            : "different"
        : c.serticaUsd == null
          ? null
          : "extra";
  const rows = d?.companies.map((c) => ({ ...c, st: statusOf(c) })) ?? [];
  const diffs = rows.filter((r) => r.st && r.st !== "match").length;
  const shown = onlyDiff ? rows.filter((r) => r.st && r.st !== "match") : rows;
  const dirty =
    !!d &&
    (d.limitUsd == null ||
      !valid ||
      !same(d.limitUsd, limit) ||
      d.companies.some((c) => c.assigned !== assigned.has(c.no)));
  const toggle = (no: string) =>
    setAssigned((s) => {
      const n = new Set(s);
      if (n.has(no)) n.delete(no);
      else n.add(no);
      return n;
    });

  return (
    <section ref={ref} className="border border-line bg-card" aria-label={`Approval limit of ${roleNo}`}>
      <div className="bg-navy text-white px-4 py-2 flex items-center gap-3">
        <span className="font-semibold">{roleNo}</span>
        <span className="text-white/80 truncate">{d?.name ?? ""}</span>
        <button className="ml-auto text-white/80 hover:text-white" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      {q.isLoading && <p className="p-4 text-muted">Loading…</p>}
      {q.isError && <p className="p-4 text-muted">This role has no approver row in Sertica and no standard.</p>}
      {d && (
        <div className="p-4 space-y-4 text-[12.5px]">
          {d.limitUsd == null ? (
            <p className="text-muted">
              No standard set yet. Filled in from Sertica: its most common limit for this role and the companies
              that have it.{me.isAdmin ? " Save to make it the standard." : ""}
            </p>
          ) : (
            <p className="text-muted">
              Set by {d.updatedBy} on {formatWibLong(new Date(d.updatedAt!))}.
            </p>
          )}

          <div className="flex flex-wrap items-end gap-4">
            <label className="space-y-1">
              <span className="label block">Approval limit (USD)</span>
              <input
                className="input w-40 tnum text-right"
                inputMode="decimal"
                value={unlimited ? "" : usd}
                placeholder={unlimited ? "Unlimited" : ""}
                disabled={!me.isAdmin || unlimited}
                onChange={(e) => setUsd(e.target.value.replace(/[^0-9.]/g, ""))}
              />
            </label>
            <label className="space-y-1">
              <span className="label block">Approval limit (IDR)</span>
              <input
                className="input w-48 tnum text-right"
                inputMode="numeric"
                value={unlimited || rate == null || !valid ? "" : Math.round(limit * rate).toLocaleString("id-ID")}
                placeholder={unlimited ? "Unlimited" : rate == null ? "No IDR rate" : ""}
                disabled={!me.isAdmin || unlimited || rate == null}
                onChange={(e) => {
                  const idr = Number(e.target.value.replace(/[^0-9]/g, ""));
                  if (rate) setUsd(String(Math.round((idr / rate) * 100) / 100));
                }}
              />
            </label>
            <label className="flex items-center gap-2 pb-1.5">
              <input type="checkbox" checked={unlimited} disabled={!me.isAdmin} onChange={(e) => setUnlimited(e.target.checked)} />
              Unlimited
            </label>
            {rate != null && (
              <span className="text-muted pb-1.5">USD 1 = Rp{rate.toLocaleString("id-ID", { maximumFractionDigits: 2 })} (Sertica&apos;s rate)</span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="font-semibold">
              Assigned to {assigned.size} of {d.companies.length} companies
            </span>
            {valid && <span className={diffs ? "cell-warn px-1.5" : "text-[var(--stage-received)]"}>{diffs ? `${diffs} differ in Sertica` : "Sertica matches"}</span>}
            {me.isAdmin && (
              <>
                <button className="text-brand hover:underline" onClick={() => setAssigned(new Set(d.companies.map((c) => c.no)))}>
                  All
                </button>
                <button
                  className="text-brand hover:underline"
                  onClick={() => setAssigned(new Set(d.companies.filter((c) => c.serticaUsd != null).map((c) => c.no)))}
                >
                  Those with the role in Sertica
                </button>
                <button className="text-brand hover:underline" onClick={() => setAssigned(new Set())}>
                  None
                </button>
              </>
            )}
            <label className="ml-auto flex items-center gap-2">
              <input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} />
              Differences only
            </label>
          </div>

          <div className="overflow-x-auto -mx-4 max-h-[420px] overflow-y-auto">
            <table className="w-full tnum">
              <thead className="sticky top-0 bg-card">
                <tr className="text-muted border-b border-line">
                  <th className="text-left font-semibold py-1 pl-4 pr-2 w-10">Assigned</th>
                  <th className="text-left font-semibold pr-2">Company</th>
                  <th className="text-left font-semibold pr-2">Name</th>
                  <th className="text-right font-semibold pr-2">Sertica limit (USD)</th>
                  <th className="text-right font-semibold pr-2">Sertica limit (IDR)</th>
                  <th className="text-left font-semibold pr-4">Against the standard</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => (
                  <tr key={c.no} className="border-b border-line">
                    <td className="py-1 pl-4 pr-2">
                      <input
                        type="checkbox"
                        aria-label={`Assign ${c.no}`}
                        checked={assigned.has(c.no)}
                        disabled={!me.isAdmin}
                        onChange={() => toggle(c.no)}
                      />
                    </td>
                    <td className="pr-2 whitespace-nowrap">{c.no}</td>
                    <td className="pr-2">{c.name ?? <span className="text-muted">Not in Sertica&apos;s companies</span>}</td>
                    <td className="pr-2 text-right whitespace-nowrap">{c.serticaUsd == null ? "—" : usdText(c.serticaUsd)}</td>
                    <td className="pr-2 text-right whitespace-nowrap">
                      {c.serticaUsd == null || rate == null ? "—" : c.serticaUsd >= UNLIMITED ? "Unlimited" : idrText(c.serticaUsd * rate)}
                    </td>
                    <td className={`pr-4 whitespace-nowrap ${c.st ? STATUS[c.st].cls : ""}`}>{c.st ? STATUS[c.st].label : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {me.isAdmin && (
            <div className="flex flex-wrap items-center gap-3">
              <button
                className="rounded-input bg-brand-2 text-white font-semibold px-3 py-1 disabled:opacity-50"
                disabled={!valid || !dirty || save.isPending}
                onClick={() => save.mutate({ limitUsd: limit, companies: [...assigned] })}
              >
                {save.isPending ? "Saving…" : "Save standard"}
              </button>
              {dirty && d.limitUsd != null && (
                <button className="text-brand hover:underline" onClick={() => reset(d)}>
                  Undo changes
                </button>
              )}
              {d.limitUsd != null && (
                <button
                  className="ml-auto text-[var(--danger)] hover:underline"
                  disabled={save.isPending}
                  onClick={() => confirm(`Remove the standard for ${roleNo}?`) && save.mutate({ limitUsd: null, companies: [] })}
                >
                  Remove standard
                </button>
              )}
              <span role="status">
                {save.isError && <span className="text-[var(--danger)]">Could not save.</span>}
                {save.isSuccess && !dirty && <span className="text-[var(--stage-received)]">Saved.</span>}
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
